import React from "react";
import PropTypes from "prop-types";
import Typography from "./ui/Typography";
import Chip from "./ui/Chip";
import PosterPlaceholder from "./ui/PosterPlaceholder";
import BridgeText from "./BridgeText";
import { cn } from "../utils/classNames";
import { formatReleaseDate } from "../utils/formatReleaseDate";
import { handleInternalLinkClick } from "../utils/navigation";
import { trackEvent } from "../utils/analytics";
import { getDisplayPlatforms } from "../utils/networkNames";

function getPoster(show) {
  return show?.posterMetadata?.thumbnail || show?.poster || show?.omdbData?.poster || null;
}

function getSummary(show) {
  const candidates = [show?.plotOverview, show?.summary, show?.omdbData?.plot];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }
  return "";
}

const ShowRecommendationCard = ({
  related,
  priority = false,
  featured = false,
  className = "",
  surface = "show_page_related",
  anchorTitle = "",
}) => {
  const slug = typeof related?.slug === "string" && related.slug.trim() ? related.slug.trim() : "";
  const title = typeof related?.title === "string" && related.title.trim() ? related.title.trim() : "Untitled show";
  const bridgeText = typeof related?.bridge === "string" && related.bridge.trim()
    ? related.bridge.trim()
    : "";
  const hasBridge = Boolean(bridgeText);
  const poster = getPoster(related);
  const summary = getSummary(related);
  const network = getDisplayPlatforms(related);
  const releaseDate = typeof related?.releaseDate === "string" ? formatReleaseDate(related.releaseDate) : "";
  const releaseYearMatch = typeof related?.releaseDate === "string"
    ? /^(\d{4})-\d{2}-\d{2}$/.exec(related.releaseDate)
    : null;
  const fallbackYear = related?.year || related?.omdbData?.year || "";
  const year = releaseYearMatch?.[1] || fallbackYear;
  const displayDate = releaseDate && year ? `${releaseDate} ${year}` : releaseDate || year;
  const metaItems = [network, displayDate].filter(Boolean);
  const genres = Array.isArray(related?.genres)
    ? related.genres.filter((genre) => typeof genre === "string" && genre.trim()).slice(0, 2)
    : [];
  const showHref = slug ? `/show/${slug}` : null;
  const trackRecommendationClick = () => {
    if (!slug) return;
    trackEvent("recommendation_click", {
      surface,
      target: "related_show",
      current_slug: slug,
    });
  };

  return (
    <article
      className={cn("show-recommendation-card", featured && "show-recommendation-card--featured", className)}
    >
      <div className="show-recommendation-card__show">
        <div className="row-span-2">
          <div className="show-recommendation-card__poster">
            {poster ? (
              <img
                src={poster}
                alt={`${title} poster`}
                className="show-recommendation-card__poster-image"
                loading="lazy"
                fetchPriority={priority ? "high" : "auto"}
              />
            ) : (
              <PosterPlaceholder
                showId={related?.id}
                className="show-recommendation-card__poster-image bg-poster-placeholder-gradient"
              />
            )}
          </div>
        </div>

        <div className="show-recommendation-card__details">
          <div className="show-recommendation-card__copy">
            {featured && <p className="text-caption font-semibold text-secondary-readable">Closest match</p>}
            <Typography as="h3" variant="h4" className="show-recommendation-card__title text-foreground">
              {showHref ? (
                <a
                  href={showHref}
                  onClick={(event) => {
                    trackRecommendationClick();
                    handleInternalLinkClick(event, showHref);
                  }}
                  className="show-recommendation-card__title-link"
                >
                  {title}
                </a>
              ) : (
                title
              )}
            </Typography>

            {metaItems.length > 0 && (
              <Typography variant="caption" className="text-label-light text-muted-foreground">
                {metaItems.join(" • ")}
              </Typography>
            )}

            {genres.length > 0 && (
              <div className="show-recommendation-card__genres">
                {genres.map((genre) => (
                  <Chip key={genre} variant="muted" size="badge" className="text-label-light">
                    {genre}
                  </Chip>
                ))}
              </div>
            )}

            {summary && (
              <Typography
                variant="body"
                className="show-recommendation-card__summary"
              >
                {summary}
              </Typography>
            )}
          </div>
        </div>
      </div>

      {hasBridge && featured && (
        <div className="show-recommendation-card__bridge">
          <div className="show-recommendation-card__eyebrow">
            <span className="show-recommendation-card__eyebrow-dot" aria-hidden="true" />
            <Typography variant="caption" className="text-primary text-label-light">
              Why it fits
            </Typography>
          </div>
          <p
            className="show-recommendation-card__bridge-text"
          >
            <BridgeText text={bridgeText} titles={[anchorTitle, title]} />
          </p>
        </div>
      )}
      {hasBridge && !featured && (
        <div className="show-recommendation-card__bridge">
          <div className="show-recommendation-card__eyebrow">
            <span className="show-recommendation-card__eyebrow-dot" aria-hidden="true" />
            <Typography variant="caption" className="text-primary text-label-light">
              Why it fits
            </Typography>
          </div>
          <p className="show-recommendation-card__bridge-text">
            <BridgeText text={bridgeText} titles={[anchorTitle, title]} />
          </p>
        </div>
      )}
    </article>
  );
};

ShowRecommendationCard.propTypes = {
  related: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    slug: PropTypes.string,
    title: PropTypes.string,
    bridge: PropTypes.string,
    poster: PropTypes.string,
    posterMetadata: PropTypes.shape({
      thumbnail: PropTypes.string,
    }),
    omdbData: PropTypes.shape({
      poster: PropTypes.string,
      plot: PropTypes.string,
      network: PropTypes.string,
      year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    plotOverview: PropTypes.string,
    summary: PropTypes.string,
    releaseDate: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sourceName: PropTypes.string,
    networks: PropTypes.arrayOf(PropTypes.string),
    genres: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  priority: PropTypes.bool,
  featured: PropTypes.bool,
  className: PropTypes.string,
  surface: PropTypes.string,
  anchorTitle: PropTypes.string,
};

export default ShowRecommendationCard;
